/**
 * «E-postvarsler» (owner, 2026-10-23): the staff member's OWN choice, per kind, of an e-mail at once, one summary at
 * 07:00, or none — from GET/PUT /admin/me/notifications. Off is the default for everyone.
 *  - Only the kinds this person may use are drawn (core rule 20: hidden, never greyed out); the server refuses the rest.
 *  - Each change is saved at once, and the status line says what was saved — or why not, with the old choice kept.
 *  - With no mail transport on the server the choices are shown disabled with one sentence why: nothing would arrive.
 * `mayChooseMail()` is the door (the same permissions the server's kinds use); `useMailNudge()` is for Oversikt: true
 * while e-mail works, the person could get something, and has switched nothing on.
 */
import React, { useEffect, useId, useState } from 'react';
import { call, can, send } from '../lib/api';
import { FormDialog } from './dialog';
import { Msg } from './ui';
import { useToastOn } from '../lib/hooks';

export type NotifyMode = 'off' | 'instant' | 'daily';
export interface NotifyKind { kind: string; label_no: string; why_no: string; allowed: boolean; mode: NotifyMode; schedule?: 'morning' }
export interface NotifyPrefs { mailConfigured: boolean; kinds: NotifyKind[] }

const MODES: [NotifyMode, string][] = [['off', 'Av'], ['instant', 'Med en gang'], ['daily', 'En gang om dagen kl. 07']];
const SAVED: Record<NotifyMode, string> = { off: 'av', instant: 'med en gang', daily: 'en gang om dagen kl. 07' };
/** Arrangementer pro (076): the morning kinds (an event soon, a task due tomorrow) are told at 08:00 — on or off. */
const MORNING: [NotifyMode, string][] = [['off', 'Av'], ['daily', 'Kl. 08 samme morgen']];

/** May this person be told about anything at all — the permission for at least one kind. */
export const mayChooseMail = () =>
  can('membership.decide') || can('store.manage') || can('requests.read') || can('push.approve') || can('notices.approve')
  || can('donations.read')    // fast giver (2026-10-31): «Faste gaver som stoppet etter mislykkede trekk»
  || can('events.write') || can('work.assigned');   // events pro's morning kinds (PM 2026-10-31): event_soon, task_due

/** For Oversikt: «Vil du få e-post når noe venter?» — only while it would help. */
export function useMailNudge(): { show: boolean; reload: () => void } {
  const [prefs, setPrefs] = useState<NotifyPrefs | null>(null);
  const load = () => { if (mayChooseMail()) call<NotifyPrefs>('/me/notifications').then(setPrefs).catch(() => setPrefs(null)); };
  useEffect(load, []);
  const allowed = prefs ? prefs.kinds.filter((k) => k.allowed) : [];
  return { show: Boolean(prefs && prefs.mailConfigured && allowed.length && allowed.every((k) => k.mode === 'off')), reload: load };
}

export function NotifyPrefsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [prefs, setPrefs] = useState<NotifyPrefs | null>(null);
  const [failed, setFailed] = useState('');
  useToastOn(failed, 'err');
  const [msg, setMsg] = useState<{ text: string; tone: '' | 'ok' | 'err' }>({ text: '', tone: '' });
  const [busy, setBusy] = useState<string | null>(null);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    setMsg({ text: '', tone: '' }); setFailed('');
    call<NotifyPrefs>('/me/notifications').then(setPrefs).catch((e) => setFailed((e as Error).message || 'Kunne ikke hente valgene.'));
  }, [open]);

  async function choose(k: NotifyKind, mode: NotifyMode) {
    if (busy || mode === k.mode) return;
    setBusy(k.kind);
    const r = await send('/me/notifications', { method: 'PUT', body: JSON.stringify({ kind: k.kind, mode }) });
    setBusy(null);
    if (r.ok) { setPrefs(r.body as NotifyPrefs); setMsg({ text: `Lagret: ${k.label_no} — ${k.schedule === 'morning' && mode !== 'off' ? 'kl. 08' : SAVED[mode]}.`, tone: 'ok' }); }
    else setMsg({ text: (r.body && (r.body.message_no || r.body.message)) || 'Ble ikke lagret. Prøv igjen.', tone: 'err' });
  }

  const kinds = prefs ? prefs.kinds.filter((k) => k.allowed) : [];
  return (
    <FormDialog open={open} title="E-postvarsler" onClose={onClose}
      hint="Få en e-post når noe venter på deg. E-posten sier bare hvor mange som venter og lenker hit — aldri navn eller detaljer.">
      {!prefs && !failed && <p className="note">Henter …</p>}
      {prefs && !prefs.mailConfigured && (
        <p className="msg err" role="status">E-post er ikke satt opp for denne moskeen ennå, så ingen e-post blir sendt. Valgene kan ikke endres før det er på plass.</p>
      )}
      {prefs && !kinds.length && <p className="note">Det er ingenting du har tilgang til som det sendes e-post om.</p>}
      {kinds.map((k) => (
        <fieldset key={k.kind} className="notify-kind" disabled={!prefs!.mailConfigured || busy === k.kind} aria-describedby={`${id}-${k.kind}`}>
          <legend>{k.label_no}</legend>
          <p className="note" id={`${id}-${k.kind}`}>{k.why_no}</p>
          <div className="notify-modes">
            {(k.schedule === 'morning' ? MORNING : MODES).map(([mode, label]) => (
              <label key={mode} className="chk">
                <input type="radio" name={`${id}-${k.kind}-mode`} value={mode} checked={k.mode === mode} onChange={() => { void choose(k, mode); }} /> {label}
              </label>
            ))}
          </div>
        </fieldset>
      ))}
      <Msg text={msg.text} tone={msg.tone} />
    </FormDialog>
  );
}
